import React, { useState, useEffect } from "react";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";
import axios from "axios";
import Select from "react-select";

const statusOptions = ["", "D/O", "P", "A"];

const generatePDFBlob = async ({ language, month, days, doctors, numDays }) => {
  const isUrdu = language === "ur";
  const hospitalName = isUrdu ? "راولپنڈی انسٹیٹیوٹ آف کارڈیالوجی" : "Rawalpindi Institute of Cardiology";
  const subAddress = isUrdu ? "راول روڈ، راولپنڈی" : "Rawal Road, Rawalpindi";
  const title = isUrdu ? "ڈاکٹر روستر" : "Doctor Roster";

  const leftLogoUrl = "../../assets/img/logo/new-logo.png";
  const rightLogoUrl = "../../assets/img/logo/punjab-logo.png";

  const headers = Array.from({ length: numDays }, (_, i) => `<th>${i + 1}</th>`).join("");

  const rowsHtml = doctors.map(doctor => {
    const rowCells = Array.from({ length: numDays }, (_, i) => `<td>${days[doctor]?.[i + 1] || ""}</td>`).join("");
    return `<tr><td><strong>${doctor}</strong></td>${rowCells}</tr>`;
  }).join("");

  const content = `
    <div style="font-family: ${isUrdu ? "'Noto Nastaliq Urdu'" : "'Segoe UI', sans-serif"}; direction: ${isUrdu ? "rtl" : "ltr"}; padding: 20px; font-size: 12px; color: #2c3e50;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
        <div style="width: 20%;"><img src="${leftLogoUrl}" style="height: 60px;" /></div>
        <div style="text-align: center; width: 60%;">
          <h2 style="margin: 0; font-weight: 700;">${hospitalName}</h2>
          <p style="margin: 2px 0; font-size: 13px; color: #555;">${subAddress}</p>
          <h4 style="margin: 6px 0; color: #444;">${title} – ${month}</h4>
        </div>
        <div style="width: 20%; text-align: ${isUrdu ? "left" : "right"};">
          <img src="${rightLogoUrl}" style="height: 60px;" />
        </div>
      </div>

      <table style="width: 100%; border-collapse: collapse; text-align: center; font-size: 12px;">
        <thead>
          <tr style="background: linear-gradient(to right, #0052D4, #4364F7); color: #fff;">
            <th>Doctor</th>${headers}
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>

      <p style="text-align:center; margin-top: 20px; font-size: 10px; color: #888;">
        © ${new Date().getFullYear()} ${hospitalName}. All rights reserved.
      </p>
    </div>
  `;

  return await html2pdf()
    .from(content)
    .set({
      margin: 0.3,
      filename: `Doctor_Roster_${month}_${language.toUpperCase()}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "landscape" }
    })
    .output("blob");
};

const WardBoyAyaITC = () => {
  const [doctorList, setDoctorList] = useState([]);
  const [doctors, setDoctors] = useState([]); // Selected doctors
  const [numDays, setNumDays] = useState(30);
  const [month, setMonth] = useState(new Date().toLocaleString("default", { month: "long", year: "numeric" }));
  const [days, setDays] = useState({});
const [newDoctorName, setNewDoctorName] = useState("");
const [newDoctorSpecialty, setNewDoctorSpecialty] = useState("");


const addDoctor = () => {
  if (!newDoctorName.trim() || !newDoctorSpecialty.trim()) {
    return Swal.fire("⚠️ Please enter name and specialty", "", "warning");
  }

  axios.post("https://ricgop.online/backend/addDoctor.php", {
    name: newDoctorName,
    specialty: newDoctorSpecialty,
  })
  .then(res => {
    Swal.fire("✅ Doctor Added", "", "success");
    // update local doctor list
    setDoctorList(prev => [...prev, { name: newDoctorName, specialty: newDoctorSpecialty }]);
    setNewDoctorName("");
    setNewDoctorSpecialty("");
  })
  .catch(() => {
    Swal.fire("❌ Error", "Could not add doctor", "error");
  });
};

useEffect(() => {
  axios.get("https://ricgop.online/backend/getDoctors.php")
    .then(res => {
      console.log("API response:", res.data);
      setDoctorList(Array.isArray(res.data) ? res.data : []);
    })
    .catch(() => setDoctorList([]));
}, []);


  // Initialize days object whenever doctors or numDays change
// ⚡ Replace your days initialization useEffect with this
useEffect(() => {
  const temp = {};
  const today = new Date();

  doctors.forEach((doc) => {
    temp[doc] = {};
    for (let i = 1; i <= numDays; i++) {
      // Calculate the actual date of this day
      const [monthName, year] = month.split(" ");
      const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
      const date = new Date(year, monthIndex, i);

      // ⚡ Default: "P", but "D/O" on Sundays
      temp[doc][i] = date.getDay() === 0 ? "D/O" : "P";
    }
  });

  setDays(temp);
}, [doctors, numDays, month]); // ⚡ include month so it updates if changed


  const toggleDoctor = (name) => {
    setDoctors(prev => prev.includes(name) ? prev.filter(d => d !== name) : [...prev, name]);
  };

  const updateDay = (doctorName, dayNum, value) => {
    setDays(prev => ({
      ...prev,
      [doctorName]: { ...prev[doctorName], [dayNum]: value }
    }));
  };

  const downloadPDFs = async () => {
    if (!doctors.length) return Swal.fire("⚠️ Select at least one doctor", "", "warning");
    Swal.fire({ title: 'Generating...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const [enBlob, urBlob] = await Promise.all([
        generatePDFBlob({ language: "en", month, days, doctors, numDays }),
        generatePDFBlob({ language: "ur", month, days, doctors, numDays })
      ]);

      const link = document.createElement("a");
      link.href = URL.createObjectURL(enBlob);
      link.download = `Doctor_Roster_${month}_EN.pdf`;
      link.click();

      const link2 = document.createElement("a");
      link2.href = URL.createObjectURL(urBlob);
      link2.download = `Doctor_Roster_${month}_UR.pdf`;
      link2.click();

      Swal.fire("✅ Success", "PDFs downloaded.", "success");
    } catch {
      Swal.fire("❌ Error", "PDF generation failed.", "error");
    }
  };

  return (
    <div className="container bg-light p-4 rounded-4 shadow border">
  <h4 className="text-center mb-3">🩺 Auto Doctor Roster Generator</h4>

  <div className="row g-3 mb-3">
    <div className="col-md-3">
      <label className="form-label fw-semibold">📅 Month</label>
      <select
        className="form-select"
        value={month}
        onChange={e => setMonth(e.target.value)}
      >
        {Array.from({ length: 12 }, (_, i) => {
          const d = new Date();
          d.setMonth(d.getMonth() + i);
          const label = d.toLocaleString("default", {
            month: "long",
            year: "numeric"
          });
          return (
            <option key={i} value={label}>
              {label}
            </option>
          );
        })}
      </select>
    </div>
    <div className="col-md-3">
      <label className="form-label fw-semibold">🗓 Number of Days</label>
      <input
        type="number"
        className="form-control"
        min={1}
        max={31}
        value={numDays}
        onChange={e => setNumDays(Number(e.target.value))}
      />
    </div>
  </div>

<div className="mb-3">
  <label className="form-label fw-semibold">👨‍⚕️ Select Doctors</label>
  <Select
    isMulti
    options={doctorList.map(doc => ({
      value: doc.name,
      label: `${doc.name} – ${doc.specialty}`
    }))}
    value={doctors.map(name => ({
      value: name,
      label: `${name} – ${doctorList.find(d => d.name === name)?.specialty || ""}`
    }))}
    onChange={(selected) => setDoctors(selected.map(opt => opt.value))}
    placeholder="Choose doctors..."
    className="basic-multi-select"
    classNamePrefix="select"
    styles={{
      control: (base) => ({
        ...base,
        borderRadius: "12px",
        borderColor: "#0d6efd",
        padding: "4px 6px",
        boxShadow: "none",
        "&:hover": { borderColor: "#0a58ca" }
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: "#e7f1ff",
        borderRadius: "8px",
        padding: "2px 6px"
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: "#0d6efd",
        fontWeight: "500"
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: "#0d6efd",
        ":hover": { backgroundColor: "#0d6efd", color: "white" }
      })
    }}
  />
</div>

{/* ➕ NEW: Add Doctor Form */}
<div className="card p-3 shadow-sm mb-3">
  <h6 className="fw-semibold mb-2">➕ Add New Doctor</h6>
  <div className="row g-2">
    <div className="col-md-4">
      <input
        type="text"
        className="form-control"
        placeholder="Doctor Name"
        value={newDoctorName}
        onChange={(e) => setNewDoctorName(e.target.value)}
      />
    </div>
    <div className="col-md-4">
      <input
        type="text"
        className="form-control"
        placeholder="Specialty"
        value={newDoctorSpecialty}
        onChange={(e) => setNewDoctorSpecialty(e.target.value)}
      />
    </div>
    <div className="col-md-4">
      <button
        className="btn btn-success w-100"
        onClick={addDoctor}
      >
        Save Doctor
      </button>
    </div>
  </div>
</div>

{doctors.length > 0 && (
  <div className="table-responsive mb-3">
    <table className="table align-middle text-center shadow-sm rounded-4 overflow-hidden">
      <thead className="bg-primary text-white">
        <tr>
          <th style={{ minWidth: "150px" }}>Doctor</th>
          {Array.from({ length: numDays }, (_, i) => (
            <th key={i}>{i + 1}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {doctors.map((doc, index) => (
          <tr key={index} className="hover-row">
            <td className="fw-semibold text-primary">
              {doc}
            </td>
            {Array.from({ length: numDays }, (_, i) => (
              <td key={i}>

              <Select
  value={days[doc]?.[i + 1] ? { value: days[doc][i + 1], label: days[doc][i + 1] } : null}
  onChange={(selected) => updateDay(doc, i + 1, selected ? selected.value : "")}
  options={statusOptions.map(opt => ({ value: opt, label: opt }))}
  isClearable
  className="status-dropdown"
  menuPortalTarget={document.body}   // 👈 FIX: render menu outside table
  styles={{
    control: (base) => ({
      ...base,
      minHeight: "32px",
      borderRadius: "8px",
      fontSize: "0.85rem",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,   // 👈 FIX: force dropdown above everything
    }),
    option: (base, { data, isFocused, isSelected }) => {
      let color = "#6c757d", bg = "#fff";
      if (data.value === "D/O") { bg = "#d4edda"; color = "#155724"; }
      if (data.value === "P")   { bg = "#d1ecf1"; color = "#0c5460"; }
      if (data.value === "A")   { bg = "#f8d7da"; color = "#721c24"; }

      return {
        ...base,
        backgroundColor: isFocused ? "#f0f0f0" : bg,
        color,
        fontWeight: isSelected ? "600" : "400",
      };
    },
    singleValue: (base, { data }) => {
      let color = "#6c757d";
      if (data.value === "D/O") color = "#155724";
      if (data.value === "P")   color = "#0c5460";
      if (data.value === "A")   color = "#721c24";
      return { ...base, color, fontWeight: "500" };
    }
  }}
/>

              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}

 {/* Inline CSS */}
      <style>{`

      /* Default (no status selected) */
.status-default {
  background-color: #ffffff !important;
  color: #6c757d;
}

/* Example status colors */
.status-On\ Duty {
  background-color: #d4edda !important; /* light green */
  color: #155724 !important;
  font-weight: 500;
}

.status-Leave {
  background-color: #f8d7da !important; /* light red */
  color: #721c24 !important;
  font-weight: 500;
}

.status-Off {
  background-color: #e2e3e5 !important; /* light gray */
  color: #383d41 !important;
  font-weight: 500;
}

.status-Training {
  background-color: #d1ecf1 !important; /* light blue */
  color: #0c5460 !important;
  font-weight: 500;
}


        .table {
  border-radius: 1rem;
  overflow: hidden;
}

.table thead {
  font-size: 0.95rem;
  letter-spacing: 0.5px;
}

.table tbody tr.hover-row:hover {
  background-color: #f8f9fa; /* light gray hover */
  transition: background-color 0.2s ease-in-out;
}

.form-select-sm {
  font-size: 0.85rem;
  padding: 2px 6px;
}

.shadow-sm {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05) !important;
}

      `}</style>

  <div>
    <button className="btn btn-primary" onClick={downloadPDFs}>
      📥 Download PDFs
    </button>
  </div>
</div>




  );
};

export default WardBoyAyaITC;
